import React, { useState, useEffect, useCallback } from 'react';
import { roomsApi, reservationsApi, paymentsApi, roomTypesApi, dashboardApi, inventoryApi } from '../../services/api';
import { genId, fmt, PAYMENT_TYPES, getCurrentTimeStr, getTodayStr, calcNights } from '../../utils/billing';
import { useNotify } from '../../context/ConfirmContext';

// Fixed (non-stock) service charge types, grouped for a clearer dropdown. 'Water Bottle'
// and 'Soft Drink' are deliberately NOT offered here anymore — they're tracked as real
// inventory items now, so staff should pick the inventory-linked version instead (it
// deducts stock; these fixed charges never did). They're kept in LEGACY_ONLY_CATEGORIES
// only so a service saved under the old name before this change still displays and edits
// correctly rather than being misread as a free-text "Other" entry.
const SERVICE_CATEGORY_GROUPS = {
    'Food & Beverage 🍽️': ['Breakfast', 'Lunch', 'Dinner'],
    'Services 🧺': ['Laundry', 'Extra Bed', 'Airport Transfer', 'Doctor on Call'],
    'Penalties & Fees ⚠️': ['Damage Penalty'],
};
const LEGACY_ONLY_CATEGORIES = ['Water Bottle', 'Soft Drink'];
const ALL_FIXED_CATEGORIES = [...Object.values(SERVICE_CATEGORY_GROUPS).flat(), ...LEGACY_ONLY_CATEGORIES];

const BLOCK_REASONS = [
    'General Maintenance 🛠️',
    'Guest Hold / Pre-booking 🛌',
    'Staff / Management Use 👥',
    'Deep Cleaning / Sanitization 🧹',
    'Broken AC / HVAC Repair ❄️',
    'Plumbing / Leakage Issues 🚰',
    'Electrical Work 🔌',
    'Painting / Renovation 🧱',
    'Other / Specific Incident ⚠️'
];

const DAY_MS = 24 * 60 * 60 * 1000;

function parseDateOnly(value) {
    if (!value) return null;
    const raw = String(value).split('T')[0];
    const [year, month, day] = raw.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
}

function toDateInputValue(date) {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function addDays(date, days) {
    if (!date) return null;
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

function maxDate(dateA, dateB) {
    if (!dateA) return dateB || null;
    if (!dateB) return dateA;
    return dateA > dateB ? dateA : dateB;
}

// reservations.extra_services comes back from the API as a JSON string (or null for
// a reservation that's never had a service added). Parsing it defensively here is
// what was missing before — without it, opening this modal always started from an
// empty list regardless of what was actually saved, and hitting Save then overwrote
// the real data with that incomplete list, silently wiping any previously added
// services (and any inventory stock they were linked to) from the reservation.
function parseExtraServices(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

// Turns a raw audit_log row into a plain-English guest-activity line — nobody
// wants to diff two whole-reservation JSON blobs to figure out what changed.
function formatActivityEvent(evt) {
    const who = evt.userName || 'Someone';
    const nv = evt.newValues || {};
    const ov = evt.oldValues || {};
    if (evt.action === 'CREATE' && evt.entityType === 'reservation') return `Checked in by ${who}`;
    if (evt.action === 'ADD_SERVICE') {
        const qtyStr = nv.quantity > 1 ? ` x${nv.quantity}` : '';
        if (nv.settlement === 'paid') {
            const method = { cash: 'Cash', upi: 'UPI', credit_card: 'Card', debit_card: 'Card' }[nv.paymentType] || nv.paymentType || 'cash';
            return `${nv.name}${qtyStr} — paid now (${method}) by ${who}`;
        }
        return `${nv.name}${qtyStr} added to bill by ${who}`;
    }
    if (evt.action === 'REMOVE_SERVICE') {
        const qtyStr = ov.quantity > 1 ? ` x${ov.quantity}` : '';
        return `${ov.name}${qtyStr} removed by ${who}`;
    }
    if (evt.action === 'ROOM_CHANGE') {
        const reasonStr = nv.reason ? ` — ${nv.reason}` : '';
        return `Moved Room ${ov.room} → Room ${nv.room} by ${who}${reasonStr}`;
    }
    if (evt.action === 'UPDATE' && nv.status === 'checked-out') return `Checked out by ${who}`;
    if (evt.action === 'UPDATE') return `Details updated by ${who}`;
    return `${evt.action} by ${who}`;
}

function getOverdueCheckoutInfo(checkoutValue) {
    const checkoutDate = parseDateOnly(checkoutValue);
    // getTodayStr() resolves in local (IST) time — parseDateOnly(new Date().toISOString())
    // used to convert to UTC first, reporting yesterday's date for several hours around
    // midnight IST and marking overnight stays as overdue a day early (or missing it).
    const today = parseDateOnly(getTodayStr());

    if (!checkoutDate || !today) {
        return {
            isOverdue: false,
            extraDays: 0,
            minDate: '',
            defaultDate: '',
            checkoutLabel: checkoutValue || 'N/A'
        };
    }

    const extraDays = Math.max(0, Math.floor((today.getTime() - checkoutDate.getTime()) / DAY_MS));
    const minDate = maxDate(checkoutDate, today);
    const defaultDate = maxDate(addDays(checkoutDate, 1), today);

    return {
        isOverdue: extraDays > 0,
        extraDays,
        minDate: toDateInputValue(minDate),
        defaultDate: toDateInputValue(defaultDate),
        checkoutLabel: checkoutDate.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        })
    };
}

export default function RoomGridPage({ hotel, onSuccess, onNavigate, onCheckout, onEdit }) {
    const notify = useNotify();
    const [roomsByFloor, setRoomsByFloor] = useState({});
    const [roomTypes, setRoomTypes] = useState([]);
    const [activeReservations, setActiveReservations] = useState([]);
    const [upcomingReservations, setUpcomingReservations] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Extend Modal State
    const [extendRes, setExtendRes] = useState(null);
    const [extendData, setExtendData] = useState({
        date: '',
        advanceAmount: '',
        paymentType: 'cash',
        last5: '',
        note: 'Extension payment',
        serviceCategory: 'Laundry',
        serviceName: '',
        serviceQuantity: 1,
        serviceUnitPrice: '',
        serviceSettlement: 'bill',
        servicePaymentType: 'cash',
        editingServiceId: null
    });
    const [processing, setProcessing] = useState(false);
    const [upcomingRes, setUpcomingRes] = useState(null); // Used for Reserved room details modal
    const [cleaningRoom, setCleaningRoom] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null); // { title, body, icon, onConfirm }
    const [vacantRoom, setVacantRoom] = useState(null);
    const [blockingRoom, setBlockingRoom] = useState(null);
    const [blockReason, setBlockReason] = useState(BLOCK_REASONS[0]);
    const [stagingServices, setStagingServices] = useState([]);
    const [servicesChanged, setServicesChanged] = useState(false);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [activityLog, setActivityLog] = useState([]);
    const [showActivityLog, setShowActivityLog] = useState(false);
    const [restockModalConfig, setRestockModalConfig] = useState(null);
    const [noRestockItemIds, setNoRestockItemIds] = useState(new Set());
    const [roomChangeModal, setRoomChangeModal] = useState(null); // { fromRoom, toRoom, reason }
    const [editRateModal, setEditRateModal] = useState(null); // { newRate }
    const [collectPaymentRows, setCollectPaymentRows] = useState(null); // array of pay rows, or null when modal is closed

    const load = useCallback(async () => {
        if (!hotel) return;
        setLoading(true);

        try {
            const [rms, activeRes, upcomingRes, rts, dStats, invItems] = await Promise.all([
                roomsApi.list(hotel.id),
                reservationsApi.list(hotel.id, 'checked-in'),
                reservationsApi.list(hotel.id, 'confirmed'),
                roomTypesApi.list(hotel.id),
                dashboardApi.stats(hotel.id),
                inventoryApi.items(hotel.id).catch(() => [])
            ]);
            setInventoryItems(invItems || []);
            
            setStats({
                totalRooms: Number(dStats.roomStats.total),
                occupied: Number(dStats.roomStats.occupied),
                vacant: Number(dStats.roomStats.vacant),
                dirty: Number(dStats.roomStats.dirty),
                occupancyRate: dStats.roomStats.total > 0 ? Math.round((dStats.roomStats.occupied / dStats.roomStats.total) * 100) : 0,
                todayIn: dStats.todayIn || [],
                todayOut: dStats.todayOut || [],
                tomorrowIn: dStats.tomorrowIn || []
            });

            const grouped = {};
            const hotelFloorsCount = Number(hotel.floors) || 0;

            // Initialize active floors
            for (let i = 1; i <= hotelFloorsCount; i++) {
                const label = i === 1 ? '1st Floor' : i === 2 ? '2nd Floor' : i === 3 ? '3rd Floor' : `${i}th Floor`;
                grouped[label] = [];
            }

            // Fill rooms
            rms.forEach(r => {
                const floorKey = r.floor || 'Unassigned';
                // Only create the floor key if it doesn't exist AND the floor name is NOT empty
                if (!grouped[floorKey]) grouped[floorKey] = [];
                grouped[floorKey].push(r);
            });

            // Clean up: If a floor key exists but is NOT in the 1..floors range AND has 0 rooms, remove it
            const activeFloorLabels = Array.from({ length: hotelFloorsCount }, (_, i) => {
                const n = i + 1;
                return n === 1 ? '1st Floor' : n === 2 ? '2nd Floor' : n === 3 ? '3rd Floor' : `${n}th Floor`;
            });

            Object.keys(grouped).forEach(key => {
                if (grouped[key].length === 0 && !activeFloorLabels.includes(key) && key !== 'Unassigned') {
                    delete grouped[key];
                }
            });

            for (const fl in grouped) {
                grouped[fl].sort((a, b) => (a.number || '').toString().localeCompare((b.number || '').toString(), 'en', { numeric: true }));
            }

            setRoomsByFloor(grouped);
            setRoomTypes(rts);

            const today = getTodayStr();
            const upcomingToday = upcomingRes.filter(r => r.check_in === today);

            const mapRes = (list) => list.map(r => ({
                ...r,
                guest: { name: r.guest_name, phone: r.guest_phone },
                checkIn: r.check_in,
                checkOut: r.check_out,
                checkInTime: r.check_in_time,
                advancePaid: r.advance_paid,
                totalAmount: r.total_amount
            }));

            setActiveReservations(mapRes(activeRes));
            setUpcomingReservations(mapRes(upcomingToday));
        } catch (err) {
            console.error('Room Grid Load Error:', err);
        }
        setLoading(false);
    }, [hotel]);

    useEffect(() => {
        load();
        const interval = setInterval(load, 15000); // refresh every 15s to keep it live
        return () => clearInterval(interval);
    }, [hotel, load]);

    const handleQuickCheckIn = async (res) => {
        if (!res) return;
        setConfirmAction({
            icon: '🛎️',
            title: 'Check-in Guest?',
            body: `Are you sure you want to check-in ${res.guest?.name} to Room ${res.room} right now?`,
            confirmText: 'Check-in Now',
            onConfirm: async () => {
                setProcessing(true);
                try {
                    await reservationsApi.update(res.id, { 
                        status: 'checked-in',
                        check_in_time: getCurrentTimeStr() 
                    });
                    setUpcomingRes(null);
                    setConfirmAction(null);
                    onSuccess?.(`Guest ${res.guest?.name || ''} checked in successfully!`);
                    load();
                } catch (err) {
                    onSuccess?.('Error: ' + err.message, 'error');
                }
                setProcessing(false);
            }
        });
    };

    const handleRoomClick = async (room, res) => {
        if (room.status === 'cleaning') {
            setCleaningRoom(room);
            return;
        }

        const isBlocked = room.status === 'blocked' || room.status === 'maintenance';

        if (res) {
            // It's occupied or reserved (upcoming today).
            if (res.status === 'checked-in') {
                // Occupied. Open extend modal.
                setExtendRes(res);
                setStagingServices(parseExtraServices(res.extra_services));
                setServicesChanged(false);
                setActivityLog([]);
                reservationsApi.activity(res.id).then(setActivityLog).catch(() => setActivityLog([]));
                const overdueInfo = getOverdueCheckoutInfo(res.checkOut || res.check_out);
                setExtendData({
                    date: overdueInfo.defaultDate || '',
                    paymentType: 'cash',
                    last5: '',
                    note: `Extension advance - Room ${room.number}`,
                    serviceCategory: 'Laundry',
                    serviceName: '',
                    serviceQuantity: 1,
                    serviceUnitPrice: '',
                    serviceSettlement: 'bill',
                    servicePaymentType: 'cash'
                });
            } else if (res.status === 'confirmed') {
                // Reserved for today. Show quick check-in details.
                setUpcomingRes(res);
            }
        } else if (isBlocked) {
            // Blocked. Show unblock confirm.
            setConfirmAction({
                icon: '🛠️',
                title: 'Release Room?',
                body: `Room ${room.number} is currently marked as ${room.status} (${room.status_note || 'No reason specified'}). Do you want to mark it as Vacant and ready for guests?`,
                confirmText: 'Mark as Vacant',
                onConfirm: async () => {
                    setProcessing(true);
                    try {
                        await roomsApi.update(room.id, { ...room, status: 'vacant', status_note: null });
                        onSuccess?.(`Room ${room.number} is now Vacant.`, 'success');
                        setConfirmAction(null);
                        load();
                    } catch (err) { await notify(err.message); }
                    setProcessing(false);
                }
            });
        } else {
            // It's vacant. Show Quick Action Menu.
            setVacantRoom(room);
        }
    };

    const handleQuickBlock = async () => {
        if (!blockingRoom) return;
        setProcessing(true);
        try {
            await roomsApi.update(blockingRoom.id, { ...blockingRoom, status: 'blocked', status_note: blockReason });
            onSuccess?.(`Room ${blockingRoom.number} has been blocked for ${blockReason}.`, 'success');
            setBlockingRoom(null);
            setVacantRoom(null); // Close vacant room menu if open
            setConfirmAction(null); // Close any confirm action if open
            load();
        } catch (err) { await notify(err.message); }
        setProcessing(false);
    };


    const handleExtendSubmit = async () => {
        if (!extendRes) return;
        setProcessing(true);
        try {
            const addedAdvance = Number(extendData.advanceAmount) || 0;
            const newOut = extendData.date;

            await reservationsApi.update(extendRes.id, {
                checkOut: newOut,
                nights: calcNights(extendRes.checkIn, newOut),
                extension: true,
                extensionDate: newOut
            });

            if (addedAdvance > 0) {
                await paymentsApi.create({
                    reservationId: extendRes.id,
                    hotelId: hotel.id,
                    type: extendData.paymentType,
                    amount: addedAdvance,
                    last5: ['upi', 'credit_card', 'debit_card'].includes(extendData.paymentType) ? extendData.last5 : null,
                    note: extendData.note
                });
            }

            onSuccess?.(`Stay extended to ${newOut} successfully!`);
            setExtendRes(null);
            load();
        } catch (err) { await notify(err.message); }
        setProcessing(false);
    };

    const handleReduceExtension = async () => {
        if (!extendRes) return;
        setProcessing(true);
        try {
            const newExtraCharges = stagingServices.reduce((sum, s) => sum + Number(s.amount), 0);
            await reservationsApi.update(extendRes.id, {
                checkOut: extendData.date,
                nights: calcNights(extendRes.checkIn, extendData.date),
                extension: true,
                extensionDate: extendData.date,
                extraServices: stagingServices,
                extraCharges: newExtraCharges
            });
            onSuccess?.(`Stay modified successfully!`);
            setExtendRes(null);
            load();
        } catch (err) { await notify(err.message); }
        setProcessing(false);
    };

    const handleRoomChangeSubmit = async () => {
        if (!extendRes || !roomChangeModal) return;
        const { fromRoom, toRoom, reason } = roomChangeModal;
        if (!toRoom) { await notify('Pick a room to move the guest into.'); return; }
        if (!reason.trim()) { await notify('Please enter a reason for the room change (e.g. AC not working).'); return; }
        setProcessing(true);
        try {
            const flatRooms = Object.values(roomsByFloor).flat();
            const fromRoomObj = flatRooms.find(r => r.number === fromRoom);
            const toRoomObj = flatRooms.find(r => r.number === toRoom);
            const currentRooms = (extendRes.room || '').split(',').map(s => s.trim()).filter(Boolean);
            const newRooms = currentRooms.map(r => r === fromRoom ? toRoom : r);

            const patch = { room: newRooms.join(', '), roomChangeReason: reason.trim() };

            // Only auto-recalculate the rate for the simple, common case: a
            // single-room booking moving to a different room type. Multi-room
            // bookings need staff to adjust the rate manually afterwards, since
            // this reservation's roomRate is a single aggregate figure with no
            // per-room breakdown to safely re-split here.
            const typeChanged = fromRoomObj && toRoomObj && fromRoomObj.room_type !== toRoomObj.room_type;
            if (typeChanged && currentRooms.length === 1) {
                const newType = roomTypes.find(t => t.name === toRoomObj.room_type);
                if (newType) {
                    patch.roomType = toRoomObj.room_type;
                    patch.roomRate = Number(newType.base_price) || 0;
                }
            }

            await reservationsApi.update(extendRes.id, patch);
            onSuccess?.(
                typeChanged && currentRooms.length > 1
                    ? `Moved to Room ${toRoom}. Room type changed — please review the rate manually.`
                    : `Moved to Room ${toRoom} successfully!`
            );
            setRoomChangeModal(null);
            setExtendRes(null);
            load();
        } catch (err) { await notify(err.message); }
        setProcessing(false);
    };

    const mkPayRow = () => ({ id: Date.now() + Math.random(), type: 'cash', last5: '', amount: '', note: '' });

    // A guest paying more mid-stay (not tied to extending the checkout date) —
    // just records payment rows against the reservation. Checkout computes its
    // balance live from the payments table, not a cached total, so this is
    // automatically reflected there with no other change needed.
    const handleCollectPaymentSubmit = async () => {
        if (!extendRes || !collectPaymentRows) return;
        const active = collectPaymentRows.filter(p => Number(p.amount) > 0);
        if (active.length === 0) { await notify('Enter an amount to record.'); return; }
        const missingRef = active.find(p => ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type) && !p.last5);
        if (missingRef) { await notify('Enter the reference (last 5 digits) for every non-cash payment.'); return; }
        setProcessing(true);
        try {
            for (const p of active) {
                await paymentsApi.create({
                    reservationId: extendRes.id, hotelId: hotel.id,
                    type: p.type, amount: Number(p.amount) || 0,
                    last5: ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type) ? p.last5 : null,
                    note: p.note || 'Additional payment collected mid-stay'
                });
            }
            const total = active.reduce((s, p) => s + (Number(p.amount) || 0), 0);
            onSuccess?.(`Collected ${fmt(total)} successfully!`);
            setCollectPaymentRows(null);
            setExtendRes(null);
            load();
        } catch (err) { await notify(err.message); }
        setProcessing(false);
    };

    // A prebooked reservation's rate is set at booking time and often needs a
    // final adjustment once the guest actually checks in (or later, if it was
    // simply missed) — this is the one place staff can correct it without
    // leaving the Room Grid for the full Reservations edit screen. The
    // backend recomputes GST/total from roomRate itself, so this alone is
    // enough to keep the bill in sync.
    const handleEditRateSubmit = async () => {
        if (!extendRes || !editRateModal) return;
        const newRate = Number(editRateModal.newRate);
        if (!Number.isFinite(newRate) || newRate <= 0) { await notify('Enter a valid rate.'); return; }
        setProcessing(true);
        try {
            await reservationsApi.update(extendRes.id, { roomRate: newRate });
            onSuccess?.(`Room rate updated to ${fmt(newRate)}/night.`);
            setEditRateModal(null);
            setExtendRes(null);
            load();
        } catch (err) { await notify(err.message); }
        setProcessing(false);
    };

    // "inv:<id>" is how an inventory-backed selection is encoded in serviceCategory —
    // keeps the existing fixed-category <select> pattern instead of introducing a
    // parallel field, so handleStartEditService/handleAddService stay one code path.
    const selectedInventoryItem = extendData.serviceCategory?.startsWith('inv:')
        ? inventoryItems.find(i => i.id === extendData.serviceCategory.slice(4))
        : null;

    // Actually stages/updates the service — split out from handleAddService so
    // both confirmation steps below (stock check, paid-now check) can reach it
    // once the user has cleared whichever of them applied.
    const commitAddService = ({ isInventory, finalName, qty, unitPrice, amount, isPaidNow }) => {
        if (extendData.editingServiceId) {
            setStagingServices(prev => prev.map(s => s.id === extendData.editingServiceId ? {
                ...s,
                name: finalName,
                quantity: qty,
                unitPrice: unitPrice,
                amount: amount,
                inventoryItemId: isInventory ? selectedInventoryItem.id : null,
                settlement: extendData.serviceSettlement,
                paymentType: isPaidNow ? extendData.servicePaymentType : undefined
            } : s));
        } else {
            const newService = {
                id: genId(),
                name: finalName,
                quantity: qty,
                unitPrice: unitPrice,
                amount: amount,
                date: new Date().toISOString(),
                inventoryItemId: isInventory ? selectedInventoryItem.id : null,
                settlement: extendData.serviceSettlement,
                paymentType: isPaidNow ? extendData.servicePaymentType : undefined
            };
            setStagingServices(prev => [...prev, newService]);
        }

        setServicesChanged(true);
        setExtendData({ ...extendData, serviceCategory: 'Laundry', serviceName: '', serviceQuantity: 1, serviceUnitPrice: '', serviceSettlement: 'bill', servicePaymentType: 'cash', editingServiceId: null });
    };

    const handleAddService = () => {
        const isInventory = !!selectedInventoryItem;
        const finalName = isInventory ? selectedInventoryItem.name
            : extendData.serviceCategory === 'Other' ? extendData.serviceName : extendData.serviceCategory;
        if (!finalName || !extendData.serviceUnitPrice) return;

        const qty = Number(extendData.serviceQuantity) || 1;
        const unitPrice = Number(extendData.serviceUnitPrice);
        const amount = qty * unitPrice;
        const isPaidNow = extendData.serviceSettlement === 'paid';
        const ctx = { isInventory, finalName, qty, unitPrice, amount, isPaidNow };

        // Native window.confirm() dialogs are unreliable in embedded/webview
        // browsers (a real report: the dialog flashed and vanished instantly on
        // one such device) — using the app's own React-rendered confirm modal
        // instead, which renders identically everywhere.
        const runPaidCheck = () => {
            if (!isPaidNow) { commitAddService(ctx); return; }
            // Paid-now items are locked the moment they're added (no edit/remove
            // afterward, on staged items too) — this is money changing hands right
            // now, not a draft, so catch mis-clicks before they become irreversible.
            const methodLabel = { cash: 'Cash', upi: 'UPI', credit_card: 'Card' }[extendData.servicePaymentType] || extendData.servicePaymentType;
            setConfirmAction({
                icon: '💰',
                title: 'Confirm Payment',
                body: `Record ₹${amount} as paid now (${methodLabel}) for ${finalName}${qty > 1 ? ` x${qty}` : ''}? This will NOT be added to the room bill, and can't be edited or removed afterward.`,
                confirmText: 'Confirm Payment',
                onConfirm: () => { setConfirmAction(null); commitAddService(ctx); },
            });
        };

        if (isInventory) {
            // Account for this same item already staged earlier in this session (not yet
            // saved) when checking stock, not just the DB quantity — otherwise adding the
            // same item twice could both look "in stock" against the same static number.
            const alreadyStaged = stagingServices
                .filter(s => s.inventoryItemId === selectedInventoryItem.id && s.id !== extendData.editingServiceId)
                .reduce((sum, s) => sum + Number(s.quantity), 0);
            const available = Number(selectedInventoryItem.quantity) - alreadyStaged;
            if (qty > available) {
                setConfirmAction({
                    icon: '📦',
                    title: 'Low Stock',
                    body: `Only ${available} ${selectedInventoryItem.unit || 'unit(s)'} of "${selectedInventoryItem.name}" left in stock. Add ${qty} anyway? Stock will go negative until restocked.`,
                    confirmText: 'Add Anyway',
                    onConfirm: () => { setConfirmAction(null); runPaidCheck(); },
                });
                return;
            }
        }

        runPaidCheck();
    };

    const isServiceLocked = (service) => service.settlement === 'paid';

    const handleDeleteService = async (serviceIdOrObj) => {
        const service = typeof serviceIdOrObj === 'object' ? serviceIdOrObj : stagingServices.find(s => s.id === serviceIdOrObj);
        if (!service) return;
        if (isServiceLocked(service)) {
            await notify('This item was already paid for and cannot be removed.');
            return;
        }

        if (service.inventoryItemId) {
            setRestockModalConfig(service);
        } else {
            setStagingServices(prev => prev.filter(s => s.id !== service.id));
            setServicesChanged(true);
            if (extendData.editingServiceId === service.id) {
                setExtendData({ ...extendData, serviceCategory: 'Laundry', serviceName: '', serviceQuantity: 1, serviceUnitPrice: '', serviceSettlement: 'bill', servicePaymentType: 'cash', editingServiceId: null });
            }
        }
    };

    const handleSaveServices = async () => {
        if (!extendRes) return;
        setProcessing(true);
        try {
            // Only bill-settled items count toward the room bill — paid-now items were
            // already collected on the spot and must never be double-charged at checkout.
            const newExtraCharges = stagingServices
                .filter(s => s.settlement !== 'paid')
                .reduce((sum, s) => sum + Number(s.amount), 0);

            const updated = await reservationsApi.update(extendRes.id, {
                extraServices: stagingServices,
                extraCharges: newExtraCharges,
                skipRestockItemIds: Array.from(noRestockItemIds)
            });

            onSuccess?.(`Room Services Updated Successfully!`, 'success');
            setServicesChanged(false);
            setNoRestockItemIds(new Set());
            // Use the server's reconciled response (authoritative addedBy/addedAt
            // stamps, locked paid items) rather than re-deriving it locally.
            setExtendRes(prev => ({ ...prev, ...updated }));
            setStagingServices(parseExtraServices(updated.extra_services));
            reservationsApi.activity(extendRes.id).then(setActivityLog).catch(() => {});
            load();
        } catch (err) { await notify(err.message); }
        setProcessing(false);
    };

    const handleStartEditService = async (service) => {
        if (isServiceLocked(service)) {
            await notify('This item was already paid for and cannot be edited.');
            return;
        }
        const isOther = !ALL_FIXED_CATEGORIES.includes(service.name) && !service.inventoryItemId;

        setExtendData({
            ...extendData,
            serviceCategory: service.inventoryItemId ? `inv:${service.inventoryItemId}` : isOther ? 'Other' : service.name,
            serviceName: isOther ? service.name : '',
            serviceQuantity: service.quantity,
            serviceUnitPrice: service.unitPrice,
            serviceSettlement: service.settlement || 'bill',
            servicePaymentType: service.paymentType || 'cash',
            editingServiceId: service.id
        });
    };

    if (loading && Object.keys(roomsByFloor).length === 0) {
        return <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>Loading Floor Plan...</div>;
    }

    // Natural sorting for floors
    const sortedFloors = Object.keys(roomsByFloor).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    // Distinct fallback palette for room types without a configured color
    const PALETTE = ['#e8b86d', '#7ec8c8', '#e87d7d', '#6db5e8', '#a8e86d', '#c87ec8', '#e8c86d', '#7db5e8'];
    const getRoomTypeColor = (typeName) => {
        const rt = roomTypes.find(t => t.name === typeName);
        if (rt?.color && rt.color !== '#888888' && rt.color !== '#888') return rt.color;
        const idx = roomTypes.findIndex(t => t.name === typeName);
        return PALETTE[idx % PALETTE.length] || '#888888';
    };
    const overdueInfo = extendRes ? getOverdueCheckoutInfo(extendRes.checkOut || extendRes.check_out) : null;

    return (
        <div style={{ padding: '0 0px' }}>
            {/* Status Cards Row */}
            {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
                    {[
                        { lbl: 'Total Inventory', val: stats.totalRooms, sub: 'Total rooms setup', icon: '🏢', color: 'var(--text)' },
                        { lbl: 'Occupied', val: stats.occupied, sub: `${stats.occupancyRate}% Occupancy`, icon: '🛏️', color: 'var(--accent)' },
                        { lbl: 'Vacant (Ready)', val: stats.vacant, sub: 'Available for sale', icon: '✨', color: 'var(--success)' },
                        { lbl: 'Dirty / MT', val: stats.dirty, sub: 'Housekeeping alert', icon: '🧹', color: 'var(--danger)' },
                    ].map((s, i) => (
                        <div key={i} className="panel" style={{ padding: '12px 16px', borderLeft: `4px solid ${s.color}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 2 }}>{s.lbl}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</div>
                                <div style={{ fontSize: 10, color: 'var(--muted2)' }}>{s.sub}</div>
                            </div>
                            <div style={{ fontSize: 20 }}>{s.icon}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Daily Activity Row */}
            {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
                    <TodayTable title="Check-ins (Today)" items={stats.todayIn} type="in" onAction={onEdit} onCheckout={onCheckout} onNavigate={onNavigate} onEdit={onEdit} />
                    <TodayTable title="Check-outs (Today)" items={stats.todayOut} type="out" onAction={(id) => onCheckout?.(id)} onCheckout={onCheckout} onNavigate={onNavigate} onEdit={onEdit} />
                    <TodayTable title="Check-ins (Tomorrow)" items={stats.tomorrowIn} type="in" onAction={onEdit} onCheckout={onCheckout} onNavigate={onNavigate} onEdit={onEdit} />
                </div>
            )}

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center', background: 'var(--s1)', padding: '12px 20px', borderRadius: 10, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Status Legend:</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}><div style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--success)', opacity: 0.2, border: '1px solid var(--success)' }}></div> Vacant</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}><div style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--danger)', opacity: 0.15, border: '1px solid var(--danger)' }}></div> Occupied</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}><div style={{ width: 14, height: 14, borderRadius: 3, background: '#f39c12', opacity: 0.2, border: '1px solid #f39c12' }}></div> Reserved</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}><div style={{ width: 14, height: 14, borderRadius: 3, background: '#7851a9', opacity: 0.2, border: '1px solid #7851a9' }}></div> Cleaning</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}><div style={{ width: 14, height: 14, borderRadius: 3, background: '#95a5a6', opacity: 0.3, border: '1px solid #95a5a6' }}></div> Blocked</div>
            </div>
            {/* Room Type Color Legend */}
            {roomTypes.length > 0 && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', background: 'var(--s1)', padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Room Types:</div>
                    {roomTypes.map((rt, idx) => {
                        const col = rt.color && rt.color !== '#888888' ? rt.color : PALETTE[idx % PALETTE.length];
                        return (
                            <div key={rt.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                                <div style={{ width: 14, height: 14, borderRadius: 3, background: col, border: `1px solid ${col}` }}></div>
                                {rt.name}
                            </div>
                        );
                    })}
                </div>
            )}

            {sortedFloors.length === 0 && (
                <div className="panel" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
                    No rooms configured yet. Add them in Settings / Admin.
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {sortedFloors.map(floor => (
                    <div key={floor} className="panel">
                        <div className="panel-head" style={{ borderBottom: '1px solid var(--border)' }}>
                            <span className="panel-title">{floor}</span>
                            <span className="panel-sub">{roomsByFloor[floor].length} Rooms</span>
                        </div>
                        <div style={{ padding: '24px 24px', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                            {roomsByFloor[floor].length === 0 ? (
                                <div style={{ fontSize: 13, color: 'var(--muted)', width: '100%', textAlign: 'center', py: 10 }}>No rooms configured on this floor yet. Go to Room Setup to add rooms.</div>
                            ) : roomsByFloor[floor].map(room => {
                                // Find if the room is occupied
                                const res = activeReservations.find(r =>
                                    r.room && r.room.split(',').map(s => s.trim()).includes(room.number)
                                );
                                const upcomingRes = upcomingReservations.find(r =>
                                    r.room && r.room.split(',').map(s => s.trim()).includes(room.number)
                                );

                                const isOccupied = !!res;
                                const isReserved = !!upcomingRes;
                                const isBlocked = room.status === 'blocked' || room.status === 'maintenance';
                                const roomOverdueInfo = res ? getOverdueCheckoutInfo(res.checkOut || res.check_out) : null;
                                const isOverdueOccupied = isOccupied && roomOverdueInfo?.isOverdue;

                                return (() => {
                                    const rtColor = getRoomTypeColor(room.room_type || room.roomType);
                                    const statusBorderColor = room.status === 'cleaning' ? '#7851a9'
                                        : isBlocked ? '#95a5a6'
                                        : isReserved ? '#f39c12'
                                        : isOccupied ? 'var(--danger)'
                                        : rtColor;
                                    const statusBg = room.status === 'cleaning' ? 'hsla(270, 30%, 50%, 0.05)' // Purple tint
                                        : isOccupied ? 'hsla(0, 70%, 60%, 0.04)' // Red tint
                                        : isReserved ? 'hsla(38, 90%, 50%, 0.05)' // Orange tint
                                        : isBlocked ? 'hsla(200, 5%, 60%, 0.05)' // Grey tint
                                        : rtColor + '0d'; // 5% tint of room type color

                                    return (
                                    <div
                                        key={room.id}
                                        className={`grid-room-card ${isOccupied ? 'occupied' : isReserved ? 'reserved' : isBlocked ? 'blocked' : 'vacant'}`}
                                        onClick={() => handleRoomClick(room, res || upcomingRes)}
                                        style={{
                                            border: `1px solid ${statusBorderColor}44`,
                                            borderTop: `4px solid ${rtColor}`,
                                            background: statusBg,
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {isOverdueOccupied && (
                                            <div
                                                title={`Checkout overdue by ${roomOverdueInfo.extraDays} day${roomOverdueInfo.extraDays > 1 ? 's' : ''}`}
                                                style={{
                                                    position: 'absolute',
                                                    top: 10,
                                                    right: 10,
                                                    minWidth: 28,
                                                    height: 28,
                                                    padding: '0 8px',
                                                    borderRadius: 999,
                                                    background: '#c0392b',
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 14,
                                                    fontWeight: 800,
                                                    boxShadow: '0 8px 18px rgba(192, 57, 43, 0.28)',
                                                    zIndex: 2
                                                }}
                                            >
                                                !
                                            </div>
                                        )}

                                        {/* Status indicator stripe on left */}
                                        <div style={{ position: 'absolute', left: 0, top: 4, bottom: 0, width: 4, background: statusBorderColor, borderRadius: '0 0 0 4px' }} />
                                        <div className="r-badge" style={{ color: rtColor, fontWeight: 700 }}>{room.room_type || room.roomType}</div>
                                        <div className="r-num">{room.number}</div>
                                        <div className="r-status">
                                            {room.status === 'cleaning' ? (
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ color: '#7851a9', fontWeight: 700, marginBottom: 8 }}>🧹 Cleaning</div>
                                                    <button
                                                        className="btn btn-sm"
                                                        style={{
                                                            fontSize: 10, background: '#7851a9', color: 'white', border: 'none', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', display: 'block', margin: '0 auto', position: 'relative', zIndex: 10
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleRoomClick(room, res);
                                                        }}
                                                    >
                                                        Mark as Clean
                                                    </button>
                                                </div>
                                            ) : isOccupied ? (
                                                <>
                                                    <div style={{ color: 'var(--danger)', fontWeight: 600 }}>Occupied</div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, margin: '4px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.guest?.name}</div>
                                                    <div style={{ fontSize: 10, opacity: 0.8, color: isOverdueOccupied ? '#c0392b' : 'var(--muted)', fontWeight: isOverdueOccupied ? 700 : 500 }}>
                                                        Out: {res.checkOut}
                                                    </div>
                                                    {isOverdueOccupied && (
                                                        <div style={{ fontSize: 10, color: '#c0392b', fontWeight: 800, marginTop: 4 }}>
                                                            Checkout overdue
                                                        </div>
                                                    )}
                                                </>
                                            ) : isReserved ? (
                                                <>
                                                    <div style={{ color: '#f39c12', fontWeight: 600 }}>Reserved</div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, margin: '4px 0' }}>{upcomingRes.guest?.name}</div>
                                                    <div style={{ fontSize: 10, opacity: 0.8, color: 'var(--muted)' }}>Arrival Today</div>
                                                </>
                                            ) : isBlocked ? (
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ color: '#95a5a6', fontWeight: 700, marginBottom: 2 }}>⚠️ Blocked</div>
                                                    <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, opacity: 0.9 }}>{room.status_note || 'For Maintenance'}</div>
                                                </div>
                                            ) : (
                                                <div style={{ color: 'var(--success)' }}>Vacant - Ready</div>
                                            )}
                                        </div>
                                    </div>
                                );
                                })();
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Extension Modal overlay */}
            {extendRes && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: 500, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                        <div style={{ background: 'var(--accent)', padding: '24px 32px', color: 'white', flexShrink: 0 }}>
                            <div style={{ fontSize: 12, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Room {extendRes.room} · {extendRes.roomType}</div>
                            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Occupancy Details</h3>
                        </div>

                        <div style={{ padding: '24px 32px', overflowY: 'auto', flex: 1 }}>
                            {overdueInfo?.isOverdue && (
                                <div style={{ marginBottom: 20, padding: '14px 16px', borderRadius: 12, border: '1px solid #f5b7b1', background: '#fff5f4' }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: '#c0392b', marginBottom: 6 }}>
                                        Checkout overdue by {overdueInfo.extraDays} day{overdueInfo.extraDays > 1 ? 's' : ''}
                                    </div>
                                    <div style={{ fontSize: 12, lineHeight: 1.6, color: '#7b241c' }}>
                                        Scheduled checkout was {overdueInfo.checkoutLabel}. Please confirm whether the guest has already checked out.
                                        If the guest is still present, update the new checkout date below and save the extension before closing this room.
                                    </div>
                                </div>
                            )}

                            {/* GUEST INFO PANEL */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, background: 'var(--s1)', padding: 16, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 24 }}>
                                <div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Guest Name</div>
                                    <div style={{ fontSize: 14, fontWeight: 700 }}>{extendRes.guest?.name || 'Unknown'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Phone Number</div>
                                    <div style={{ fontSize: 14, fontWeight: 700 }}>{extendRes.guest?.phone || 'N/A'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Check-in Date & Time</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{extendRes.checkIn} at {extendRes.checkInTime}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Scheduled Checkout</div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>{extendRes.checkOut}</div>
                                </div>
                            </div>

                            {/* EXTENSION OPTIONS */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, borderBottom: '1px solid var(--border)', paddingBottom: 8, marginBottom: 4 }}>
                                    {overdueInfo?.isOverdue ? 'Guest Still Present? Update Stay' : extendRes.extension ? 'Manage Extension' : 'Extend Stay'}
                                </div>

                                <div className="form-row-2">
                                    <div className="field">
                                        <label>{extendRes.extension ? 'New Checkout Date *' : 'Extended Checkout Date *'}</label>
                                        <input
                                            type="date"
                                            value={extendData.date}
                                            min={overdueInfo?.minDate || toDateInputValue(parseDateOnly(extendRes.originalCheckOut || extendRes.checkIn))}
                                            onChange={e => setExtendData({ ...extendData, date: e.target.value })}
                                        />
                                        {overdueInfo?.isOverdue ? (
                                            <div style={{ fontSize: 10, color: '#c0392b', marginTop: 4 }}>
                                                Extension date cannot be earlier than today because the scheduled checkout has already passed.
                                            </div>
                                        ) : Boolean(extendRes.extension) && extendRes.originalCheckOut && (
                                            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                                                Original Checkout was {extendRes.originalCheckOut}
                                            </div>
                                        )}
                                    </div>
                                    <div className="field">
                                        <label>Additional Advance (₹)</label>
                                        <input type="number" placeholder="0.00" value={extendData.advanceAmount} onChange={e => setExtendData({ ...extendData, advanceAmount: e.target.value })} />
                                    </div>
                                </div>

                                {Number(extendData.advanceAmount) > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Method of Advance</div>
                                        <div className="pay-type-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                                            {PAYMENT_TYPES.filter(pt => ['cash', 'upi', 'credit_card'].includes(pt.value)).map(pt => (
                                                <button key={pt.value} className={`pay-type-btn ${extendData.paymentType === pt.value ? 'selected' : ''}`} onClick={() => setExtendData(f => ({ ...f, paymentType: pt.value }))} style={{ padding: '8px 4px' }}>
                                                    <span style={{ fontSize: 12 }}>{pt.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                        <div className="field" style={{ marginTop: 12 }}>
                                            <label style={{ fontSize: 10, opacity: 0.8 }}>Remarks (optional)</label>
                                            <input value={extendData.note === 'Extension payment' ? '' : extendData.note} onChange={e => setExtendData(f => ({ ...f, note: e.target.value || 'Extension payment' }))} placeholder="Reference / note" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* EXTRA SERVICES SECTION */}
                            <div style={{ marginTop: 24, padding: '16px', background: 'var(--s1)', borderRadius: 10, border: '1px solid var(--border)' }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 12 }}>Room Services & Extras</div>
                                {(() => {
                                    const svcs = stagingServices;
                                    return svcs.length > 0 ? (
                                        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {svcs.map(s => {
                                                const d = new Date(s.addedAt || s.date);
                                                const formattedDate = !isNaN(d) ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
                                                const locked = isServiceLocked(s);
                                                const paidLabel = { cash: 'Cash', upi: 'UPI', credit_card: 'Card' }[s.paymentType] || s.paymentType;
                                                return (
                                                    <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, background: 'var(--bg)', padding: '10px 14px', borderRadius: 8, border: extendData.editingServiceId === s.id ? '2px solid var(--accent)' : '1px solid var(--border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <span style={{ fontWeight: 600 }}>
                                                                {s.inventoryItemId && <span title="Deducted from stock" style={{ marginRight: 4 }}>📦</span>}
                                                                {s.name} {s.quantity > 1 ? `(x${s.quantity})` : ''}
                                                                {locked ? (
                                                                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--success)', background: 'rgba(46,204,113,0.12)', padding: '2px 6px', borderRadius: 4 }}>✅ Paid ({paidLabel})</span>
                                                                ) : (
                                                                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--muted)', background: 'var(--bg2)', padding: '2px 6px', borderRadius: 4 }}>🧾 Billed</span>
                                                                )}
                                                            </span>
                                                            <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                                                                {formattedDate}{s.addedByName ? ` · by ${s.addedByName}` : ''}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                                                            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{fmt(s.amount)}</span>
                                                            {locked ? (
                                                                <span title="Already paid — can't be edited or removed" style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, opacity: 0.6 }}>🔒</span>
                                                            ) : (
                                                                <div style={{ display: 'flex', gap: 4 }}>
                                                                    <button title="Edit" onClick={() => handleStartEditService(s)} style={{ border: 'none', background: 'var(--bg2)', color: 'var(--accent)', cursor: 'pointer', width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                                                                        <span style={{ fontSize: 13 }}>✏️</span>
                                                                    </button>
                                                                    <button title="Delete" onClick={() => handleDeleteService(s)} style={{ border: 'none', background: 'rgba(231,76,60,0.08)', color: 'var(--danger)', cursor: 'pointer', width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                                                                        <span style={{ fontSize: 13 }}>🗑️</span>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                                                {servicesChanged ? (
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={handleSaveServices}
                                                        disabled={processing}
                                                        style={{ background: 'var(--accent)', color: 'white', fontSize: 12, fontWeight: 800, padding: '6px 16px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}
                                                    >
                                                        💾 SAVE ALL SERVICES
                                                    </button>
                                                ) : <div />}
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                                                        Total Extras (Billed): {fmt(stagingServices.filter(s => s.settlement !== 'paid').reduce((sum, s) => sum + Number(s.amount), 0))}
                                                    </div>
                                                    {stagingServices.some(s => s.settlement === 'paid') && (
                                                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                                                            Paid Directly: {fmt(stagingServices.filter(s => s.settlement === 'paid').reduce((sum, s) => sum + Number(s.amount), 0))} (not on bill)
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : null;
                                })()}
                                
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                    <div className="field" style={{ flex: '1 1 30%', marginBottom: 0 }}>
                                        <label style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>Service Type</label>
                                        <select value={extendData.serviceCategory} onChange={e => {
                                            const val = e.target.value;
                                            const invItem = val.startsWith('inv:') ? inventoryItems.find(i => i.id === val.slice(4)) : null;
                                            setExtendData({
                                                ...extendData,
                                                serviceCategory: val,
                                                // Auto-fill from the item's guest sell price (falling back to cost) so
                                                // pricing stays consistent across staff — still editable below.
                                                serviceUnitPrice: invItem
                                                    ? String(invItem.sell_price ?? invItem.cost_per_unit ?? '')
                                                    : extendData.serviceUnitPrice
                                            });
                                        }}>
                                            {(() => {
                                                 const guestInventoryItems = inventoryItems.filter(i => (i.item_type || i.itemType || 'guest') !== 'room');
                                                 return guestInventoryItems.length > 0 ? (
                                                     <optgroup label="📦 Guest Consumables (Inventory)">
                                                         {guestInventoryItems.map(item => (
                                                             <option key={item.id} value={`inv:${item.id}`}>
                                                                 {item.name} ({item.quantity} {item.unit || 'pcs'} in stock)
                                                             </option>
                                                         ))}
                                                     </optgroup>
                                                 ) : null;
                                             })()}
                                            {Object.entries(SERVICE_CATEGORY_GROUPS).map(([groupLabel, cats]) => (
                                                <optgroup key={groupLabel} label={groupLabel}>
                                                    {cats.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                                </optgroup>
                                            ))}
                                            <optgroup label="✏️ Custom">
                                                <option value="Other">Other</option>
                                            </optgroup>
                                        </select>
                                        {selectedInventoryItem && (
                                            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                                                📦 Sold from stock — {selectedInventoryItem.quantity} {selectedInventoryItem.unit || 'pcs'} currently on hand.
                                            </div>
                                        )}
                                    </div>
                                    {extendData.serviceCategory === 'Other' && (
                                        <div className="field" style={{ flex: '1 1 30%', marginBottom: 0 }}>
                                            <label style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>Item Name</label>
                                            <input type="text" placeholder="e.g. Snack" value={extendData.serviceName} onChange={e => setExtendData({...extendData, serviceName: e.target.value})} />
                                        </div>
                                    )}
                                    <div className="field" style={{ flex: '0 1 60px', marginBottom: 0 }}>
                                        <label style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>Qty</label>
                                        <input type="number" min="1" value={extendData.serviceQuantity} onChange={e => setExtendData({...extendData, serviceQuantity: e.target.value})} />
                                    </div>
                                    <div className="field" style={{ flex: '1 1 100px', marginBottom: 0 }}>
                                        <label style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>Unit Price (₹)</label>
                                        <input type="number" placeholder="0" value={extendData.serviceUnitPrice} onChange={e => setExtendData({...extendData, serviceUnitPrice: e.target.value})} />
                                    </div>
                                    <div className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
                                        <label style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>Settlement</label>
                                        <select value={extendData.serviceSettlement} onChange={e => setExtendData({ ...extendData, serviceSettlement: e.target.value })}>
                                            <option value="bill">Add to Bill</option>
                                            <option value="paid">Paid Now</option>
                                        </select>
                                    </div>
                                    {extendData.serviceSettlement === 'paid' && (
                                        <div className="field" style={{ flex: '1 1 110px', marginBottom: 0 }}>
                                            <label style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>Paid Via</label>
                                            <select value={extendData.servicePaymentType} onChange={e => setExtendData({ ...extendData, servicePaymentType: e.target.value })}>
                                                <option value="cash">Cash</option>
                                                <option value="upi">UPI</option>
                                                <option value="credit_card">Card</option>
                                            </select>
                                        </div>
                                    )}
                                    <button
                                        className="btn btn-sm"
                                        disabled={!(extendData.serviceCategory === 'Other' ? extendData.serviceName : extendData.serviceCategory) || !extendData.serviceUnitPrice || processing}
                                        style={{ background: extendData.editingServiceId ? 'var(--accent)' : 'var(--success)', color: 'white', border: 'none', height: 40, padding: '0 20px', alignSelf: 'flex-end', marginBottom: 0, fontWeight: 700 }}
                                        onClick={handleAddService}
                                    >
                                        {extendData.editingServiceId ? 'Update' : 'Add'}
                                    </button>
                                    {extendData.editingServiceId && (
                                        <button
                                            className="btn btn-sm btn-ghost"
                                            style={{ height: 40, alignSelf: 'flex-end', marginBottom: 0 }}
                                            onClick={() => setExtendData({ ...extendData, serviceCategory: 'Laundry', serviceName: '', serviceQuantity: 1, serviceUnitPrice: '', serviceSettlement: 'bill', servicePaymentType: 'cash', editingServiceId: null })}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 8 }}>
                                    {extendData.serviceSettlement === 'paid'
                                        ? "\"Paid Now\" items are collected on the spot and won't be added to the guest's checkout bill."
                                        : "These items will be added to the guest's checkout bill."}
                                </div>
                            </div>

                            {/* ACTIVITY LOG */}
                            <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--s1)', borderRadius: 10, border: '1px solid var(--border)' }}>
                                <div
                                    onClick={() => setShowActivityLog(v => !v)}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                                >
                                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>
                                        Guest Activity {activityLog.length > 0 ? `(${activityLog.length})` : ''}
                                    </div>
                                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{showActivityLog ? '▲ Hide' : '▼ Show'}</span>
                                </div>
                                {showActivityLog && (
                                    activityLog.length > 0 ? (
                                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                                            {activityLog.map(evt => {
                                                const d = new Date(evt.createdAt);
                                                const when = !isNaN(d) ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
                                                return (
                                                    <div key={evt.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, gap: 10 }}>
                                                        <span style={{ color: 'var(--text)' }}>{formatActivityEvent(evt)}</span>
                                                        <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{when}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>No activity recorded yet.</div>
                                    )
                                )}
                            </div>

                            {/* ACTION BUTTONS */}
                            <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                <button
                                    className="btn btn-ghost"
                                    style={{ flex: '1 1 100%', border: '1px solid var(--border2)', fontWeight: 700, color: 'var(--accent)' }}
                                    onClick={() => setExtendRes(null)}
                                >
                                    Cancel & Close Window
                                </button>

                                <button
                                    className="btn"
                                    style={{ flex: 1, background: 'var(--success)', border: 'none', color: 'white' }}
                                    onClick={() => onCheckout(extendRes.id)}
                                >
                                    Go to Checkout 🚪
                                </button>

                                <button
                                    className="btn btn-ghost"
                                    style={{ flex: '1 1 100%', border: '1px solid var(--border2)', fontWeight: 700 }}
                                    onClick={() => {
                                        const rooms = (extendRes.room || '').split(',').map(s => s.trim()).filter(Boolean);
                                        setRoomChangeModal({ fromRoom: rooms[0] || '', toRoom: '', reason: '' });
                                    }}
                                >
                                    🔁 Change Room
                                </button>

                                <button
                                    className="btn btn-ghost"
                                    style={{ flex: '1 1 100%', border: '1px solid var(--border2)', fontWeight: 700, color: 'var(--success)' }}
                                    onClick={() => setCollectPaymentRows([mkPayRow()])}
                                >
                                    💰 Collect Payment
                                </button>

                                <button
                                    className="btn btn-ghost"
                                    style={{ flex: '1 1 100%', border: '1px solid var(--border2)', fontWeight: 700 }}
                                    onClick={() => setEditRateModal({ newRate: String(extendRes.room_rate ?? '') })}
                                >
                                    ✏️ Edit Rate
                                </button>

                                {extendRes.extension ? (
                                    <button
                                        className="btn btn-gold"
                                        style={{ flex: 1.5 }}
                                        onClick={handleReduceExtension}
                                        disabled={processing}
                                    >
                                        {processing ? 'Saving...' : 'Update Stay Date ✅'}
                                    </button>
                                ) : (
                                    <button
                                        className="btn btn-gold"
                                        style={{ flex: 2 }}
                                        onClick={handleExtendSubmit}
                                        disabled={processing}
                                    >
                                        {processing ? 'Processing...' : 'Confirm Extension ✅'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Collect Payment Modal — for a guest paying more mid-stay, decoupled from Extend Stay */}
            {collectPaymentRows && extendRes && (
                <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }} onClick={() => setCollectPaymentRows(null)}>
                    <div className="modal-content" style={{ maxWidth: 460, borderRadius: 20, padding: '32px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>💰 Collect Payment</h2>
                        <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 13 }}>
                            Room {extendRes.room} · {extendRes.guest?.name || 'Guest'} — record money the guest is paying now, without changing their checkout date.
                        </p>

                        {collectPaymentRows.map((p, idx) => {
                            const isNonCash = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type);
                            return (
                                <div key={p.id} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>💳 Payment {idx + 1}</span>
                                        {collectPaymentRows.length > 1 && (
                                            <button type="button" onClick={() => setCollectPaymentRows(prev => prev.filter(r => r.id !== p.id))}
                                                style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>✕ Remove</button>
                                        )}
                                    </div>
                                    <div className="pay-type-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                                        {PAYMENT_TYPES.map(pt => (
                                            <button key={pt.value} type="button"
                                                className={`pay-type-btn ${p.type === pt.value ? 'selected' : ''}`}
                                                onClick={() => setCollectPaymentRows(prev => prev.map(r => r.id === p.id ? { ...r, type: pt.value, last5: '' } : r))}
                                                style={{ padding: '8px 4px', fontSize: 10 }}>
                                                <div style={{ fontSize: 16 }}>{pt.icon}</div>
                                                <div style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>{pt.label}</div>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="form-row-2" style={{ marginTop: 12 }}>
                                        <div className="field">
                                            <label>Amount (₹) *</label>
                                            <input type="number" min="0" value={p.amount} onChange={e => setCollectPaymentRows(prev => prev.map(r => r.id === p.id ? { ...r, amount: e.target.value } : r))} />
                                        </div>
                                        {isNonCash && (
                                            <div className="field">
                                                <label>{p.type === 'cheque' ? 'Cheque No. (Last 5)' : p.type === 'bank_transfer' ? 'Transaction ID (Last 5)' : 'Ref. / UPI / Card (Last 5)'} *</label>
                                                <input value={p.last5} onChange={e => setCollectPaymentRows(prev => prev.map(r => r.id === p.id ? { ...r, last5: e.target.value.replace(/\D/g, '').slice(0, 5) } : r))} maxLength={5} placeholder="e.g. 48291" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
                                        <label>Remarks <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
                                        <input value={p.note} onChange={e => setCollectPaymentRows(prev => prev.map(r => r.id === p.id ? { ...r, note: e.target.value } : r))} placeholder="e.g. extra bed charge collected" />
                                    </div>
                                </div>
                            );
                        })}

                        <button type="button" className="btn btn-sm btn-ghost" style={{ marginBottom: 16 }} onClick={() => setCollectPaymentRows(prev => [...prev, mkPayRow()])}>+ Add another payment method</button>

                        <div style={{ display: 'flex', gap: 12 }}>
                            <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border2)' }} onClick={() => setCollectPaymentRows(null)}>Cancel</button>
                            <button className="btn btn-gold" style={{ flex: 1.5 }} onClick={handleCollectPaymentSubmit} disabled={processing}>
                                {processing ? 'Recording...' : 'Record Payment ✅'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Rate Modal */}
            {editRateModal && extendRes && (
                <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }} onClick={() => setEditRateModal(null)}>
                    <div className="modal-content" style={{ maxWidth: 400, borderRadius: 20, padding: 32 }} onClick={e => e.stopPropagation()}>
                        <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>✏️ Edit Room Rate</h2>
                        <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 13 }}>
                            Room {extendRes.room} · {extendRes.guest?.name || 'Guest'} — corrects the per-night rate for the whole stay; GST and total recalculate automatically.
                        </p>
                        <div className="field">
                            <label>Rate / Night (₹) *</label>
                            <input type="number" min="0" value={editRateModal.newRate} onChange={e => setEditRateModal({ newRate: e.target.value })} autoFocus />
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                            <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border2)' }} onClick={() => setEditRateModal(null)}>Cancel</button>
                            <button className="btn btn-gold" style={{ flex: 1.5 }} onClick={handleEditRateSubmit} disabled={processing}>
                                {processing ? 'Saving...' : 'Save Rate ✅'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Room Modal */}
            {roomChangeModal && extendRes && (() => {
                const currentRooms = (extendRes.room || '').split(',').map(s => s.trim()).filter(Boolean);
                const flatRooms = Object.values(roomsByFloor).flat();
                const vacantRooms = flatRooms.filter(r => r.status === 'vacant').sort((a, b) => (a.number || '').localeCompare(b.number || '', 'en', { numeric: true }));
                return (
                    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }} onClick={() => setRoomChangeModal(null)}>
                        <div className="modal-content" style={{ maxWidth: 440, borderRadius: 20, padding: '32px' }} onClick={e => e.stopPropagation()}>
                            <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>🔁 Change Room</h2>
                            <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 13 }}>
                                Move the guest to a different vacant room — e.g. AC not working, guest request, maintenance issue.
                            </p>

                            {currentRooms.length > 1 && (
                                <div className="field">
                                    <label>Which room is being changed?</label>
                                    <select value={roomChangeModal.fromRoom} onChange={e => setRoomChangeModal(m => ({ ...m, fromRoom: e.target.value }))}>
                                        {currentRooms.map(rn => <option key={rn} value={rn}>Room {rn}</option>)}
                                    </select>
                                </div>
                            )}

                            <div className="field">
                                <label>Move to Vacant Room *</label>
                                <select value={roomChangeModal.toRoom} onChange={e => setRoomChangeModal(m => ({ ...m, toRoom: e.target.value }))}>
                                    <option value="">Select a room…</option>
                                    {vacantRooms.map(r => (
                                        <option key={r.id} value={r.number}>Room {r.number} · {r.room_type}</option>
                                    ))}
                                </select>
                                {vacantRooms.length === 0 && (
                                    <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>No vacant rooms available right now.</div>
                                )}
                            </div>

                            <div className="field">
                                <label>Reason *</label>
                                <input type="text" placeholder="e.g. AC not working" value={roomChangeModal.reason} onChange={e => setRoomChangeModal(m => ({ ...m, reason: e.target.value }))} />
                            </div>

                            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                                <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border2)' }} onClick={() => setRoomChangeModal(null)}>Cancel</button>
                                <button className="btn btn-gold" style={{ flex: 1.5 }} onClick={handleRoomChangeSubmit} disabled={processing || !roomChangeModal.toRoom}>
                                    {processing ? 'Moving...' : 'Confirm Move ✅'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Cleaning Confirm Modal */}
            {cleaningRoom && (
                <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }}>
                    <div className="modal-content" style={{ maxWidth: 400, borderRadius: 28, padding: '40px 32px', textAlign: 'center', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
                        <div style={{ fontSize: 56, marginBottom: 24 }}>🧹</div>
                        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Room Cleaned?</h2>
                        <p style={{ margin: '12px 0 32px', color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
                            Are you sure you want to change Room {cleaningRoom.number} from cleaning to vacant and ready to occupy state?
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <button className="btn btn-ghost" style={{ borderRadius: 16, height: 54, fontWeight: 700, border: '1.5px solid var(--border)', background: 'white' }} onClick={() => setCleaningRoom(null)}>Cancel</button>
                            <button className="btn" style={{ borderRadius: 16, height: 54, fontWeight: 800, background: 'var(--success)', color: 'white', boxShadow: '0 10px 20px rgba(46, 204, 113, 0.2)' }} onClick={async () => {
                                try {
                                    await roomsApi.update(cleaningRoom.id, { ...cleaningRoom, status: 'vacant' });
                                    onSuccess?.(`Room ${cleaningRoom.number} is now Vacant.`, 'success');
                                    setCleaningRoom(null);
                                    load();
                                } catch (err) {
                                    onSuccess?.('Failed to update room status.', 'error');
                                }
                            }}>Confirm Ready</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Restock Inventory Confirmation Modal */}
            {restockModalConfig && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="modal-content" style={{ maxWidth: 440, width: '90%', padding: 28, borderRadius: 24, textAlign: 'center', boxShadow: '0 30px 80px rgba(0,0,0,0.5)', background: 'var(--bg1, #ffffff)', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 52, marginBottom: 12 }}>📦</div>
                        <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>Re-add to Inventory Stock?</h3>
                        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24, lineHeight: 1.5 }}>
                            You are removing <strong>{restockModalConfig.quantity} x {restockModalConfig.name}</strong> from Room {extendRes?.room || ''}. Should these items be restored back to your inventory stock count?
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <button
                                className="btn"
                                style={{ width: '100%', height: 46, fontWeight: 800, fontSize: 13, background: 'var(--teal)', color: 'white', borderRadius: 12, cursor: 'pointer' }}
                                onClick={() => {
                                    const item = restockModalConfig;
                                    setStagingServices(prev => prev.filter(s => s.id !== item.id));
                                    setServicesChanged(true);
                                    setRestockModalConfig(null);
                                    onSuccess?.(`Removed ${item.quantity} x ${item.name} & scheduled stock restoration (+${item.quantity}). Click "Save All Services" to apply.`, 'success');
                                }}
                            >
                                🔄 Yes, Restock to Inventory (+{restockModalConfig.quantity})
                            </button>
                            <button
                                className="btn"
                                style={{ width: '100%', height: 46, fontWeight: 800, fontSize: 13, background: 'var(--danger)', color: 'white', borderRadius: 12, cursor: 'pointer' }}
                                onClick={() => {
                                    const item = restockModalConfig;
                                    if (item.inventoryItemId) {
                                        setNoRestockItemIds(prev => new Set([...prev, item.inventoryItemId]));
                                    }
                                    setStagingServices(prev => prev.filter(s => s.id !== item.id));
                                    setServicesChanged(true);
                                    setRestockModalConfig(null);
                                    onSuccess?.(`Removed ${item.quantity} x ${item.name} (marked as consumed/wasted — stock will not be restored). Click "Save All Services" to apply.`, 'warning');
                                }}
                            >
                                🗑️ No, Item Consumed / Wasted (Do Not Restock)
                            </button>
                            <button
                                className="btn btn-ghost"
                                style={{ width: '100%', height: 38, opacity: 0.7, marginTop: 4, cursor: 'pointer' }}
                                onClick={() => setRestockModalConfig(null)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Confirm Modal (Generic) */}
            {confirmAction && (
                // Must render above the base .modal-overlay z-index (9999, from
                // index.css) since this confirm can be triggered from on top of
                // another already-open modal (e.g. the Occupancy Details modal) —
                // it was previously getting rendered behind that modal instead of
                // on top of it.
                <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 10000 }}>
                    <div className="modal-content" style={{ maxWidth: 400, borderRadius: 28, padding: '40px 32px', textAlign: 'center', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
                        <div style={{ fontSize: 56, marginBottom: 24 }}>{confirmAction.icon || '❓'}</div>
                        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>{confirmAction.title}</h2>
                        <p style={{ margin: '12px 0 32px', color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
                            {confirmAction.body}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <button className="btn btn-ghost" style={{ borderRadius: 16, height: 54, fontWeight: 700, border: '1.5px solid var(--border)', background: 'white' }} onClick={() => setConfirmAction(null)}>Cancel</button>
                            <button className="btn" style={{ borderRadius: 16, height: 54, fontWeight: 800, background: 'var(--success)', color: 'white', boxShadow: '0 10px 20px rgba(46, 204, 113, 0.2)' }} onClick={confirmAction.onConfirm}>
                                {confirmAction.confirmText || 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upcoming Reservation Details Modal */}
            {upcomingRes && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: 400, padding: 0, overflow: 'hidden' }}>
                        <div style={{ background: '#f39c12', padding: '20px 24px', color: 'white' }}>
                            <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, opacity: 0.8 }}>Reserved Arrival Today</div>
                            <h3 style={{ margin: 0, fontSize: 20 }}>Room {upcomingRes.room}</h3>
                        </div>
                        <div style={{ padding: 24 }}>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700 }}>GUEST NAME</div>
                                <div style={{ fontSize: 15, fontWeight: 700 }}>{upcomingRes.guest?.name}</div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                <div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700 }}>PHONE</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{upcomingRes.guest?.phone}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700 }}>SLOT</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{upcomingRes.checkInTime}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setUpcomingRes(null)}>Close</button>
                                <button 
                                    className="btn btn-gold" 
                                    style={{ flex: 2, background: '#f39c12', color: 'white', border: 'none' }}
                                    disabled={processing}
                                    onClick={() => handleQuickCheckIn(upcomingRes)}
                                >
                                    {processing ? 'Processing...' : 'Check-in Now 🛎️'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Quick Action Modal for Vacant Rooms */}
            {vacantRoom && (
                <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(10px)', zIndex: 10000 }} onClick={() => setVacantRoom(null)}>
                    <div className="modal-content" style={{ maxWidth: 360, padding: 0, borderRadius: 24, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <div style={{ background: 'var(--accent)', padding: '24px', color: '#fff', textAlign: 'center' }}>
                            <div style={{ fontSize: 13, textTransform: 'uppercase', fontWeight: 700, opacity: 0.8, letterSpacing: '0.1em' }}>{vacantRoom.room_type || vacantRoom.roomType}</div>
                            <h3 style={{ margin: 0, fontSize: 32, fontWeight: 900 }}>Room {vacantRoom.number}</h3>
                        </div>
                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <button className="btn" style={{ height: 50, background: 'var(--success)', border: 'none', color: '#fff', fontWeight: 800, fontSize: 15 }} onClick={() => {
                                setVacantRoom(null);
                                onNavigate?.('checkin');
                            }}>🚀 Direct Check-in</button>
                            
                            <button className="btn btn-ghost" style={{ height: 50, border: '1.5px solid var(--border2)', color: 'var(--muted)', fontWeight: 600, fontSize: 15 }} onClick={() => {
                                setVacantRoom(null);
                                setBlockingRoom(vacantRoom);
                            }}>⚠️ Block / Maintenance</button>
                            
                            <button className="btn btn-sm btn-ghost" style={{ marginTop: 8, height: 40 }} onClick={() => setVacantRoom(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Blocking Reason Modal */}
            {blockingRoom && (
                <div className="modal-overlay" style={{ zIndex: 10000 }}>
                    <div className="modal-content" style={{ maxWidth: 400 }}>
                         <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
                            <h2 style={{ margin: 0, fontWeight: 800 }}>Block Room {blockingRoom.number}</h2>
                            <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 13 }}>Please specify a reason for pulling this room out of sales.</p>
                        </div>

                        <div className="field">
                            <label>Reason for Hold</label>
                            <select value={blockReason} onChange={e => setBlockReason(e.target.value)}>
                                {BLOCK_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginTop: 32 }}>
                            <button className="btn btn-ghost" style={{ height: 50, fontWeight: 700 }} onClick={() => setBlockingRoom(null)}>Cancel</button>
                            <button className="btn" style={{ height: 50, background: 'var(--danger)', border: 'none', color: '#fff', fontWeight: 800 }} onClick={handleQuickBlock} disabled={processing}>
                                {processing ? 'Blocking...' : 'Confirm Block 🔒'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TodayTable({ title, items = [], type, onAction, onCheckout, onNavigate, onEdit }) {
    // Ensure unique items by ID so duplicate records never appear in tables
    const uniqueItems = Array.isArray(items) ? items.filter((item, index, self) =>
      index === self.findIndex((t) => t.id === item.id)
    ) : [];

    return (
      <div className="panel">
        <div className="panel-head" style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <span className="panel-title" style={{ fontSize: 12 }}>{title}</span>
            <span className="panel-sub" style={{ fontSize: 10 }}>{uniqueItems.length} {type === 'in' ? 'pending' : 'due'}</span>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 180, overflowY: 'auto' }}>
          <table className="tbl-sm">
            <tbody>
              {uniqueItems.length === 0 ? (
                <tr><td colSpan={3}><div style={{ padding: '20px 0', fontSize: 11, textAlign: 'center', color: 'var(--muted)' }}>None scheduled</div></td></tr>
              ) : uniqueItems.map(r => {
                const isCheckedOut = r.status === 'checked-out';
                const isCheckedIn = r.status === 'checked-in';
                const isPreBooking = r.status === 'confirmed' || r.status === 'reserved';
                return (
                  <tr key={r.id} style={{ background: isPreBooking ? 'rgba(232,184,109,0.07)' : 'transparent' }}>
                    <td style={{ padding: '6px 12px' }}>
                      {isPreBooking && (
                        <span style={{ fontSize: 9, background: 'rgba(232,184,109,0.25)', color: 'var(--gold)', padding: '1px 5px', borderRadius: 6, fontWeight: 800, border: '1px solid rgba(232,184,109,0.4)', display: 'inline-block', marginBottom: 2 }}>
                          📋 PRE-BOOKED
                        </span>
                      )}
                      <div style={{ fontSize: 11, fontWeight: 700 }}>{r.guest_name || r.guestName || r.name || '—'}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)' }}>{r.phone || r.guestPhone || r.guest_phone || ''}</div>
                    </td>
                    <td style={{ padding: '6px 12px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700 }}>{r.room || 'Unassigned'}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)' }}>
                        {isPreBooking
                          ? <span style={{ color: 'var(--gold)', fontWeight: 700 }}>Awaiting Check-in</span>
                          : isCheckedIn
                            ? <span style={{ color: 'var(--success)' }}>In-House</span>
                            : isCheckedOut
                              ? <span style={{ color: 'var(--muted)', fontWeight: 700 }}>Checked Out</span>
                              : r.roomType}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 12px' }}>
                      {isCheckedOut ? (
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', padding: '3px 8px', background: 'var(--s1)', borderRadius: 4, border: '1px solid var(--border)', display: 'inline-block' }}>
                          Checked Out
                        </span>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, justifyItems: 'flex-end', justifyContent: 'flex-end' }}>
                          {isPreBooking ? (
                            <button
                              className="btn btn-sm btn-gold"
                              style={{ fontSize: 10, padding: '2px 10px' }}
                              onClick={() => onEdit ? onEdit(r.id) : onNavigate?.('checkin')}
                              title="Complete Check-in for this pre-booking"
                            >
                              ✔ Check In
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-ghost"
                              style={{ fontSize: 10, padding: '2px 8px', border: '1px solid var(--border)' }}
                              onClick={() => onEdit ? onEdit(r.id) : onNavigate?.('reservations')}
                              title="Edit Reservation Details"
                            >
                              ✏️ Edit
                            </button>
                          )}
                          {type === 'out' && (
                            <button
                              className="btn btn-sm btn-gold"
                              style={{ fontSize: 10, padding: '2px 8px' }}
                              onClick={() => onAction(r.id)}
                              title="Proceed to Checkout"
                            >
                              🚪 Checkout
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
}
